import React, { useState, KeyboardEvent } from "react";
import { X } from "lucide-react";
import { Input } from "./input";
import { Badge } from "./badge";

interface TagInputProps {
    value: string[];
    onChange: (tags: string[]) => void;
    placeholder?: string;
    className?: string;
    error?: boolean;
}

export function TagInput({
    value = [],
    onChange,
    placeholder = "Type and press Enter",
    className = "",
    error = false,
}: TagInputProps) {
    const [inputValue, setInputValue] = useState("");

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === "," || e.key === " ") {
            e.preventDefault();
            addTag();
        } else if (e.key === "Tab") {
            // Add tag when user tabs to next field
            addTag();
        } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
            // Remove last tag if input is empty and backspace is pressed
            const newTags = [...value];
            newTags.pop();
            onChange(newTags);
        }
    };

    const addTag = () => {
        const trimmedValue = inputValue.trim();
        if (trimmedValue && !value.includes(trimmedValue)) {
            onChange([...value, trimmedValue]);
            setInputValue("");
        }
    };

    const removeTag = (indexToRemove: number) => {
        onChange(value.filter((_, index) => index !== indexToRemove));
    };

    const handleBlur = () => {
        // Add tag when user moves to a different form field
        addTag();
    };

    return (
        <div
            className={`min-h-[40px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${error ? "border-red-500 focus-within:ring-red-500" : ""
                } ${className}`}
        >
            <div className="flex flex-wrap gap-1">
                {value.map((tag, index) => (
                    <Badge
                        key={index}
                        variant="secondary"
                        className="mr-1 mb-1"
                    >
                        {tag}
                        <button
                            type="button"
                            className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            onClick={() => removeTag(index)}
                        >
                            <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                        </button>
                    </Badge>
                ))}
                <Input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    placeholder={value.length === 0 ? placeholder : ""}
                    className="flex-1 min-w-[120px] border-0 p-0 h-6 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none"
                />
            </div>
        </div>
    );
}
